import { useState } from "react";

const grantData = [
  { name: "문화체육관광부 문화예술진흥기금", received: 25000, allocated: 22000, remaining: 3000, period: "2026 상반기" },
  { name: "서울시 생활문화 활성화 지원", received: 8000, allocated: 7200, remaining: 800, period: "2026년도" },
  { name: "한국문화예술위원회 창작지원", received: 0, allocated: 0, remaining: 0, period: "신청중" },
];

const expenditures = [
  { category: "사업비", budget: 28000, spent: 21500, type: "직접비" },
  { category: "인건비 (사무국)", budget: 6000, spent: 4800, type: "간접비" },
  { category: "후원 행사비", budget: 2000, spent: 1200, type: "간접비" },
  { category: "일반관리비", budget: 3000, spent: 2100, type: "간접비" },
];

export default function FoundationFinance() {
  const [tab, setTab] = useState<"pl" | "grants" | "spend">("pl");

  const totalReceived = grantData.reduce((s, g) => s + g.received, 0);
  const totalAllocated = expenditures.reduce((s, e) => s + e.spent, 0);
  const surplus = totalReceived - totalAllocated;

  const plItems = [
    { label: "정부보조금수익", type: "수익", amount: 25000 },
    { label: "기업후원금수익", type: "수익", amount: 10000 },
    { label: "개인기부금수익", type: "수익", amount: 2300 },
    { label: "이자수익", type: "수익", amount: 180 },
    { label: "사업비 지출", type: "비용", amount: -21500 },
    { label: "인건비", type: "비용", amount: -4800 },
    { label: "후원행사비", type: "비용", amount: -1200 },
    { label: "일반관리비", type: "비용", amount: -2100 },
    { label: "세금과공과", type: "비용", amount: -320 },
  ];

  const totalRevenue = plItems.filter(i => i.type === "수익").reduce((s, i) => s + i.amount, 0);
  const totalExpense = plItems.filter(i => i.type === "비용").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-4">
      {/* 비영리 안내 배너 */}
      <div className="bg-[#4B6CF7]/[0.06] border border-[#4B6CF7]/15 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-[#4B6CF7]/50 text-base mt-0.5 shrink-0"></i>
        <div className="text-xs text-[#3D3D3D] leading-relaxed">
          <span className="text-[#666666] font-medium">비영리 회계 기준 적용</span> — 이익 추구가 목적이 아닌 보조금·기부금 수입과 사업 지출 구조로 운영됩니다.
          잉여금은 차기 사업으로 이월됩니다.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.05]">
        {([["pl", "ri-file-chart-line", "수지계산서"], ["grants", "ri-government-line", "보조금 현황"], ["spend", "ri-pie-chart-line", "지출 분석"]] as const).map(([id, icon, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              tab === id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
            }`}>
            <i className={`${icon} text-sm`}></i>{label}
          </button>
        ))}
      </div>

      {tab === "pl" && (
        <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.05]">
            <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">수지계산서 (2026년 1~3월)</span>
          </div>
          <div className="p-5 space-y-1">
            <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">수 익</div>
            {plItems.filter(i => i.type === "수익").map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5">
                <span className="text-[#666666] text-xs pl-3">{item.label}</span>
                <span className="text-[#CCCCCC] text-xs tabular-nums">{item.amount.toLocaleString()}만원</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-white/[0.05] pt-2 mt-1">
              <span className="text-[#888888] text-xs font-semibold">수익 합계</span>
              <span className="text-[#F2F2F2] text-sm font-semibold tabular-nums">{totalRevenue.toLocaleString()}만원</span>
            </div>

            <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2 mt-5">비 용</div>
            {plItems.filter(i => i.type === "비용").map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5">
                <span className="text-[#666666] text-xs pl-3">{item.label}</span>
                <span className="text-white/40 text-xs tabular-nums">{Math.abs(item.amount).toLocaleString()}만원</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-white/[0.05] pt-2 mt-1">
              <span className="text-[#888888] text-xs font-semibold">비용 합계</span>
              <span className="text-[#888888] text-sm font-semibold tabular-nums">{Math.abs(totalExpense).toLocaleString()}만원</span>
            </div>

            <div className={`flex items-center justify-between border-t-2 pt-3 mt-2 ${surplus >= 0 ? "border-[#4B6CF7]/20" : "border-white/10"}`}>
              <span className="text-[#888888] text-sm font-semibold">당기 잉여금 (이월)</span>
              <span className={`text-base font-bold tabular-nums ${surplus >= 0 ? "text-[#7A9BFF]" : "text-white/40"}`}>
                {surplus >= 0 ? "+" : ""}{(totalRevenue + totalExpense).toLocaleString()}만원
              </span>
            </div>
          </div>
        </div>
      )}

      {tab === "grants" && (
        <div className="space-y-3">
          {grantData.map(g => {
            const pct = g.received > 0 ? Math.round((g.allocated / g.received) * 100) : 0;
            return (
              <div key={g.name} className="bg-[#111112] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-[#CCCCCC] text-xs font-semibold">{g.name}</div>
                  <span className="text-[#3D3D3D] text-[10px]">{g.period}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <div className="text-[#3D3D3D] text-[10px]">수령액</div>
                    <div className="text-[#888888] text-sm font-medium tabular-nums">{g.received.toLocaleString()}만원</div>
                  </div>
                  <div>
                    <div className="text-[#3D3D3D] text-[10px]">집행액</div>
                    <div className="text-[#CCCCCC] text-sm font-medium tabular-nums">{g.allocated.toLocaleString()}만원</div>
                  </div>
                  <div>
                    <div className="text-[#3D3D3D] text-[10px]">잔액</div>
                    <div className="text-[#7A9BFF] text-sm font-medium tabular-nums">{g.remaining.toLocaleString()}만원</div>
                  </div>
                </div>
                {g.received > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white/[0.04] rounded-full h-1">
                      <div className="h-1 rounded-full bg-[#4B6CF7]/50" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[#444444] text-[11px]">{pct}% 집행</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "spend" && (
        <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.05]">
            <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">지출 유형별 분석</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.04]">
                {["비목", "구분", "예산", "집행", "비율"].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {expenditures.map(e => {
                const pct = Math.round((e.spent / e.budget) * 100);
                return (
                  <tr key={e.category} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-[#CCCCCC]">{e.category}</td>
                    <td className="px-5 py-3 text-[#555555]">{e.type}</td>
                    <td className="px-5 py-3 text-[#888888] tabular-nums">{e.budget.toLocaleString()}만원</td>
                    <td className="px-5 py-3 text-[#CCCCCC] tabular-nums">{e.spent.toLocaleString()}만원</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-white/[0.04] rounded-full h-1">
                          <div className="h-1 rounded-full bg-[#4B6CF7]/50" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[#444444]">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
